import { observer } from 'mobx-react'
import { FC, useCallback, useContext, useMemo, useState } from 'react'
import './DisplayDocumentCore.css'
import { PdfViewer } from '@components/Generic'
import { StoreContext } from '@store/Root.store'
import { useDisplayDocument } from '@/hooks/useDisplayDocument'
import type { EditableField } from '../SignDocumentCommon/types'
import { buildEditableFieldsConfig } from './helpers'
import { SignicatBranding } from '@components/SignicatBranding/SignicatBranding'
import { PageHeader } from '../SignDocumentCommon/PageHeader/PageHeader'
import { actionReport } from '@/methods/actionReport'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { LoadingScreen } from '../Generic/PdfViewer/core/LoadingScreen'
import '../Generic/PdfViewer/core/Core.css'
import { Buttons } from '../SignDocumentCommon/Buttons/Buttons'

interface DisplayDocumentCoreProps {
  service: string
  editableFields?: EditableField[]
  transOverwrite?: {
    header?: string
    subHeader?: string
    proceed?: string
    transSignature?: {
      header: string
      pressSignDocument?: string
      yourSignature?: string
      signDocument?: string
    }
  }
}

export const DisplayDocumentCore: FC<DisplayDocumentCoreProps> = observer(
  ({ service, editableFields: editableFieldsProp, transOverwrite }) => {
    const store = useContext(StoreContext)
    const { isMobile } = store.AppState
    const { signDocumentPage, pdfViewer: transViewer } = store.TranslationsState.translations
    const trans = { ...signDocumentPage, ...transOverwrite }

    const [documentError, setDocumentError] = useState<string | null>(null)
    const [allowSigning, setAllowSigning] = useState<boolean>(true)

    const {
      isLoading,
      isDocReady,
      btnLoading,
      pdfFile,
      fileList,
      currentDocIndex,
      documentScrolled,
      setDocumentScrolled,
      checkboxKey,
      defaultCheckboxChecked,
      setDefaultCheckboxChecked,
      handleSign,
      isButtonDisabled,
      editableFields,
      fieldValues,
      setFieldValue,
      registerResolvedFields,
    } = useDisplayDocument(service, editableFieldsProp)

    const documentFields = useMemo(
      () => editableFields.filter((field) => field.documentId === pdfFile?.id),
      [editableFields, pdfFile?.id]
    )

    const editableFieldsConfig = buildEditableFieldsConfig({
      fields: documentFields,
      values: fieldValues,
      onChange: setFieldValue,
      requiredErrorLabel: trans.requiredErrorLabel,
      ibanErrorLabel: trans.ibanErrorLabel,
      onFieldsResolved: registerResolvedFields,
    })

    const handleCheckboxChange = useCallback(() => {
      if (!pdfFile?.id) return

      setDefaultCheckboxChecked(!defaultCheckboxChecked)

      if (!defaultCheckboxChecked) {
        actionReport({
          type: 'event.onboarding-web.agreement.AGREEMENT_ACKNOWLEDGED',
          payload: {
            name: pdfFile.name,
          },
        })
      }
    }, [setDefaultCheckboxChecked, defaultCheckboxChecked, pdfFile])

    return (
      <>
        <div className="display-doc-core__container sign-document-core">
          <PageHeader
            header={trans.header}
            fileList={fileList || []}
            currentDocIndex={currentDocIndex ?? 0}
          />

          {isLoading || !isDocReady ? (
            <LoadingScreen>
              <div className="pdf-core__loading-text">{transViewer?.loading}</div>
            </LoadingScreen>
          ) : (
            <PdfViewer
              file={pdfFile?.url || ''}
              fileId={pdfFile?.id}
              fileUrl={pdfFile?.url}
              initialScale={isMobile ? 1.2 : 0.8}
              onDocumentLastPage={() => {
                if (!documentScrolled && pdfFile?.id) {
                  setDocumentScrolled(true)

                  actionReport({
                    type: 'event.onboarding-web.agreement.AGREEMENT_SCROLLED_TO_THE_END',
                    payload: {
                      documentId: pdfFile.id,
                      name: pdfFile.name,
                    },
                  })
                }
              }}
              documentError={documentError}
              setDocumentError={(error: string | null) => {
                setDocumentError(error)
                setAllowSigning(false)
              }}
              onDocumentLoadSuccess={() => setAllowSigning(true)}
              setAllowSigning={setAllowSigning}
              service={service}
              editableFields={editableFieldsConfig}
            />
          )}

          <div className="display-doc-core__footer">
            <div className="display-doc-core__footer__checkbox-form">
              <label
                className="label-checkbox"
                onKeyDown={(event) => onKeyDownHandler(event, handleCheckboxChange)}
                tabIndex={0}
              >
                <input
                  key={'checkbox-' + checkboxKey}
                  type="checkbox"
                  name="sign"
                  value={defaultCheckboxChecked.toString()}
                  checked={defaultCheckboxChecked}
                  id="sign"
                  onChange={handleCheckboxChange}
                  tabIndex={-1}
                />
                <span>{trans.checkboxLabel}</span>
              </label>
            </div>

            <div className="display-doc-core__footer-bottom">
              {!isMobile && (
                <SignicatBranding width="fit-content" margin="0" marginMobile="0" onSignDocument />
              )}
              <Buttons
                documentId={pdfFile?.id ?? ''}
                defaultCheckboxChecked={defaultCheckboxChecked}
                checkboxClauseEnabled={false}
                clauseCheckboxChecked={false}
                showButtonHint={false}
                handleSign={handleSign}
                setAllowSigning={setAllowSigning}
                isButtonDisabled={isButtonDisabled || !allowSigning}
                btnLoading={btnLoading}
                transProceed={trans.proceed}
                transReject={{
                  rejectButtonText: trans.rejectButtonText,
                  rejectPopupTitle: trans.rejectPopupTitle,
                  backButtonText: trans.backButtonText,
                  confirmButtonText: trans.confirmButtonText,
                }}
                transHint={trans.hint}
                service={service}
              />
            </div>
          </div>
        </div>
      </>
    )
  }
)
